import React, { createContext, useContext, useEffect, useState } from 'react';
import { setSessionToken, logout as apiLogout, getSessionToken } from '../config/api';
import { setTelemetryUser, clearTelemetryUser } from '../config/telemetry';

interface User {
  id: string;
  name: string;
  email: string;
  staff_no: string;
}

interface LoginData {
  user: User;
  session_token?: string;
  expires_in?: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (data: User | LoginData) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialize user synchronously from sessionStorage to prevent race conditions
  // Previously, user was null and restored in useEffect, causing a flash where
  // isAuthenticated=false triggered API calls before auth was restored
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = sessionStorage.getItem('user');
    const storedToken = getSessionToken();

    if (storedUser && storedToken) {
      // Both user and token exist - restore session
      try {
        return JSON.parse(storedUser);
      } catch {
        // Invalid JSON - don't restore
        return null;
      }
    }

    if (storedUser && !storedToken) {
      // Legacy session without token - clear it
      console.log('Legacy session detected (no session token) - forcing re-login');
      sessionStorage.removeItem('user');
    }

    return null;
  });

  const login = (data: User | LoginData) => {
    // Handle both old format (User) and new format (LoginData with session_token)
    let userData: User;

    if ('user' in data) {
      // New format: { user: {...}, session_token: '...', expires_in: ... }
      userData = data.user;
      if (data.session_token && data.expires_in) {
        setSessionToken(data.session_token, data.expires_in);
      }
    } else {
      // Old format: just the user object (backwards compatible)
      userData = data;
    }

    setUser(userData);
    // Store user in session storage for persistence
    sessionStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = async () => {
    // Call backend logout endpoint to invalidate session
    await apiLogout();

    setUser(null);
    // Clear user from session storage
    sessionStorage.removeItem('user');

    // Clear PKCE values if they exist
    sessionStorage.removeItem('pkce_challenge');
    sessionStorage.removeItem('pkce_verifier');

    // Redirect to Microsoft logout endpoint to clear the Microsoft session
    const microsoftLogoutUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(window.location.origin + '/login')}`;
    window.location.href = microsoftLogoutUrl;
  };

  // Note: Session restoration is now done synchronously in useState initializer
  // to prevent race conditions where API calls were made before auth was restored

  // Tag telemetry with the signed-in staff number. One effect covers all three
  // ways `user` changes — fresh login, restore-from-sessionStorage on reload,
  // and logout — so no call site has to remember to do it.
  //
  // staff_no rather than name/email: enough to trace one person's session when
  // they report something slow, without shipping a staff directory into
  // telemetry. No-op when telemetry is not configured.
  useEffect(() => {
    if (user?.staff_no) {
      setTelemetryUser(user.staff_no);
    } else {
      clearTelemetryUser();
    }
  }, [user]);

  const value = {
    user,
    isAuthenticated: !!user,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
